/**
 * preemption.ts (Jarvis Phase 3 binding 2, 2026-08-27)
 *
 * The section watch: when the operator opens a section, the assistant has
 * ALREADY read that section's work-stream and leads with the one thing that
 * matters, plus an action card. Everything here is a pure composition over
 * the get-work-stream v29 response (the SAME feed the bell's Work tab
 * renders; nothing is recomputed or invented) and the page's own envelope.
 *
 * Honesty laws, per the operator's nod:
 *   - a group the feed marks sourced:false is said to be uncomputable, in
 *     plain words, never silently skipped when it is the section's core;
 *   - anything the platform cannot compute yet (Andrew's "what changed since
 *     yesterday" needs a daily snapshot that does not exist) is stated as
 *     exactly that, never approximated;
 *   - no figures are derived from the DOM; counts come from the feed, network
 *     state comes from the page's published envelope (the same one-context
 *     law as everything else).
 *
 * Named acceptance scenarios (both real people, tested in preemption.test.mjs):
 *   SHANNON (sales/onboarding) opens Leads: what is waiting, what is stale,
 *   next action as a card.
 *   ANDREW (network) opens Network: what is down, what changed since
 *   yesterday (honest gap), what is unmonitored.
 *
 * No em-dashes; no internal codename in operator-facing strings.
 */

import type { PlatformContext } from './platformContext';
import { readOutageEvents } from './situation';

/* Loose view of the get-work-stream v29 response; every read is defensive so
   a missing group degrades to an honest gap, never a throw. */
export interface WorkStreamGroup {
  sourced?: boolean;
  advisory?: boolean;
  count?: number;
  evidenced_count?: number;
  needs_biller_check_count?: number;
  dollars_at_risk?: number;
  dollars_billed?: number;
  items?: Array<Record<string, unknown>>;
  reason?: string;
}
export type WorkStream = Record<string, WorkStreamGroup | undefined>;

export interface SectionWatch {
  /** The one-paragraph lead the panel (and a live call) opens with. */
  line: string;
  /** Registry-shaped blocks (metric_chips / options), validated by the caller
   *  through the SAME uiRegistry as every other block before render. */
  blocks: Array<Record<string, unknown>>;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days since the oldest `created_at` among items; null when nothing
 *  carries a readable timestamp (then staleness is honestly not computable). */
export function oldestAgeDays(items: Array<Record<string, unknown>> | undefined, now: number): number | null {
  if (!Array.isArray(items) || !items.length) return null;
  let oldest: number | null = null;
  for (const it of items) {
    const t = Date.parse(String((it as any)?.created_at ?? ''));
    if (isFinite(t) && (oldest == null || t < oldest)) oldest = t;
  }
  if (oldest == null) return null;
  return Math.max(0, Math.floor((now - oldest) / DAY_MS));
}

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

function group(ws: WorkStream, key: string): WorkStreamGroup {
  const g = ws?.[key];
  return g && typeof g === 'object' ? g : {};
}

function chips(heading: string, list: Array<{ label: string; value: string; variant?: string }>) {
  return { type: 'metric_chips', props: { heading, chips: list } };
}
function options(heading: string, actions: Array<{ label: string; action: string; params: Record<string, unknown> }>) {
  return { type: 'options', props: { heading }, actions };
}

/**
 * Compose the watch for a section from the feed + envelope. Returns null for
 * sections with no watch defined (the panel opens as before) and for a feed
 * that is entirely absent. `now` is injected for testability.
 */
export function composeSectionWatch(
  section: string | null,
  ws: WorkStream | null,
  ctx: PlatformContext,
  now: number = Date.now(),
): SectionWatch | null {
  if (!section || !ws || typeof ws !== 'object') return null;

  if (section === 'leads') {
    const leads = group(ws, 'leads_pending');
    if (leads.sourced === false) {
      return { line: `I cannot compute the waiting-lead queue right now: ${String(leads.reason || 'the feed does not source it')}.`, blocks: [] };
    }
    const n = typeof leads.count === 'number' ? leads.count : 0;
    if (n === 0) return { line: 'No leads are waiting right now; the queue is clear.', blocks: [] };
    const age = oldestAgeDays(leads.items, now);
    const staleness = age == null
      ? 'I cannot compute how long they have waited (the feed rows carry no timestamps I can read).'
      : `The oldest has waited ${days(age)}.`;
    return {
      line: `${n} lead${n === 1 ? '' : 's'} ${n === 1 ? 'is' : 'are'} waiting with no subscriber attached. ${staleness}`,
      blocks: [
        chips('Leads waiting', [
          { label: 'Waiting', value: String(n), variant: n > 0 ? 'warn' : 'ok' },
          ...(age != null ? [{ label: 'Oldest', value: days(age), variant: age >= 7 ? 'danger' : 'muted' }] : []),
        ]),
        options('Next action', [
          { label: 'Work the oldest lead', action: 'ask', params: { prompt: 'Who is the oldest waiting lead and what is the next step to move them?' } },
        ]),
      ],
    };
  }

  if (section === 'appointments') {
    const b = group(ws, 'no_engineer');
    if (b.sourced === false) {
      return { line: `I cannot compute the unassigned bookings right now: ${String(b.reason || 'the feed does not source it')}.`, blocks: [] };
    }
    const n = typeof b.count === 'number' ? b.count : 0;
    if (n === 0) return { line: 'Every booking has an engineer assigned; nothing is waiting.', blocks: [] };
    const age = oldestAgeDays(b.items, now);
    const staleness = age == null
      ? 'I cannot compute how long the oldest has waited (no readable timestamps on the feed rows).'
      : `The oldest has waited ${days(age)}.`;
    return {
      line: `${n} booking${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} an engineer. ${staleness}`,
      blocks: [
        chips('Bookings without an engineer', [
          { label: 'Unassigned', value: String(n), variant: 'warn' },
          ...(age != null ? [{ label: 'Oldest', value: days(age), variant: age >= 7 ? 'danger' : 'muted' }] : []),
        ]),
        options('Next action', [
          { label: 'Show the oldest unassigned booking', action: 'ask', params: { prompt: 'Which unassigned booking has waited longest and what do we know about it?' } },
        ]),
      ],
    };
  }

  if (section === 'network') {
    // What is DOWN comes from the page's own envelope (one context, never a
    // second query path); what is UNMONITORED comes from the feed.
    const os: any = ctx?.on_screen || {};
    const down = typeof os.down === 'number' && isFinite(os.down) ? os.down : null;
    const downTotal = typeof os.down_total === 'number' && isFinite(os.down_total) ? os.down_total : null;
    const events = readOutageEvents(ctx);
    const nt = group(ws, 'no_telemetry');
    const parts: string[] = [];
    if (down != null) {
      parts.push(down > 0
        ? `${down}${downTotal != null ? ` of ${downTotal}` : ''} monitored units are down right now${events.length ? `, with ${events.length} active outage event${events.length === 1 ? '' : 's'} (${events.slice(0, 2).map((e) => e.area_label || e.area_key).filter(Boolean).join(', ')})` : ''}.`
        : 'Nothing monitored is down right now.');
    } else {
      parts.push('The page has not published its down count yet, so I cannot state it.');
    }
    // Honest gap, stated as exactly what it is (spec A commissions the fix).
    parts.push('What changed since yesterday: I cannot compute that yet; no daily snapshot exists to compare against (commissioned as a rollup spec, not built).');
    if (nt.sourced === false) {
      parts.push(`Unmonitored lines: I cannot compute them right now: ${String(nt.reason || 'the feed does not source it')}.`);
    } else {
      const n = typeof nt.count === 'number' ? nt.count : 0;
      parts.push(n > 0
        ? `${n} paying line${n === 1 ? '' : 's'} ha${n === 1 ? 's' : 've'} no telemetry: we cannot see them, which is not evidence either way.`
        : 'Every paying line has telemetry.');
    }
    const blocks: Array<Record<string, unknown>> = [];
    const chipList: Array<{ label: string; value: string; variant?: string }> = [];
    if (down != null) chipList.push({ label: 'Down now', value: String(down), variant: down > 0 ? 'danger' : 'ok' });
    if (typeof nt.count === 'number') chipList.push({ label: 'No telemetry', value: String(nt.count), variant: 'muted' });
    if (chipList.length) blocks.push(chips('Network watch', chipList));
    if (down != null && down > 0) {
      blocks.push(options('Next action', [
        { label: 'Open the Resolve board', action: 'navigate', params: { section: 'resolve' } },
      ]));
    }
    return { line: parts.join(' '), blocks };
  }

  if (section === 'billing') {
    const np = group(ws, 'never_provisioned');
    const bc = group(ws, 'biller_contradiction');
    const ev = typeof np.evidenced_count === 'number' ? np.evidenced_count : 0;
    const risk = typeof np.dollars_at_risk === 'number' ? np.dollars_at_risk : 0;
    const contradictions = typeof bc.count === 'number' ? bc.count : 0;
    if (ev === 0 && contradictions === 0) {
      return { line: 'No evidenced provisioning gaps and no biller contradictions are open right now.', blocks: [] };
    }
    const parts: string[] = [];
    if (ev > 0) parts.push(`${ev} account${ev === 1 ? '' : 's'} paid without a provisioning record on evidence the biller corroborates, $${risk.toFixed(2)} at risk.`);
    if (contradictions > 0) parts.push(`${contradictions} biller contradiction${contradictions === 1 ? '' : 's'} need${contradictions === 1 ? 's' : ''} an operator to reconcile.`);
    return {
      line: parts.join(' '),
      blocks: [chips('Billing watch', [
        ...(ev > 0 ? [{ label: 'Evidenced gaps', value: String(ev), variant: 'danger' }] : []),
        ...(risk > 0 ? [{ label: 'At risk', value: `$${Math.round(risk)}`, variant: 'warn' }] : []),
        ...(contradictions > 0 ? [{ label: 'Contradictions', value: String(contradictions), variant: 'warn' }] : []),
      ])],
    };
  }

  if (section === 'tickets') {
    const h = group(ws, 'handoff_failed');
    if (h.sourced === false) {
      return { line: `I cannot compute failed handoffs right now: ${String(h.reason || 'the feed does not source it')}.`, blocks: [] };
    }
    const n = typeof h.count === 'number' ? h.count : 0;
    if (n === 0) return { line: 'No ticket handoffs are in a failed state.', blocks: [] };
    return {
      line: `${n} ticket handoff${n === 1 ? '' : 's'} to the biller ${n === 1 ? 'is' : 'are'} in a failed state and will not retry themselves.`,
      blocks: [chips('Tickets watch', [{ label: 'Failed handoffs', value: String(n), variant: 'danger' }])],
    };
  }

  return null;
}
